import { resetButtonStylingCSS } from '@tmh-website/styles/global';
import StyledTypo from '@tmh-website/styles/typo';
import styled, { css } from 'styled-components';
import { ButtonHoverThemeColor, buttonHoverThemes, ButtonThemeColor, buttonThemes } from './Button.theme';

export interface ButtonProps {
  $themeColorColor: ButtonThemeColor;
  $isLoading?: boolean;
  $isSmall?: boolean;
  $hoverThemeColor: ButtonHoverThemeColor;
  $fullWidth: boolean;
  $minWidth: boolean;
  ref?: any;
}

/**
 * Button component
 *
 * There is a weird render bug that leads to some pixel of the base button shining through
 * the hover element on the edge of the border radius.
 * To fix that the hover element is a total of 0.5 px wider and higher than the original button.
 * This could lead to line break issues in very rare cases.
 */

export const ButtonBase = css<{ $isSmall?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: ${props => (props.$isSmall === true ? props.theme.btnSmallMinHeight : props.theme.btnMinHeight)};
  margin: 0;
  line-height: 1.1;
  text-align: center;
  cursor: pointer;
  transition: background ${props => props.theme.transitionTiming};
`;

export const ButtonHover = styled.div<Pick<ButtonProps, '$isLoading' | '$isSmall' | '$hoverThemeColor'>>`
  ${StyledTypo.h6Css};
  ${ButtonBase};
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  width: calc(100% + 2px);
  height: 100%;
  min-height: ${props => (props.$isSmall === true ? props.theme.btnSmallMinHeight : props.theme.btnMinHeight)};
  padding: ${props => props.theme.btnHoverPaddingMobile};
  margin: 0 !important;
  clip-path: polygon(0 0, 0 0, -14px 101%, 0 101%);
  color: ${props => props.theme.color[buttonHoverThemes[props.$hoverThemeColor].text]};
  pointer-events: none;
  background-color: ${props => props.theme.color[buttonHoverThemes[props.$hoverThemeColor].background.normal]};
  transition: clip-path 0.5s ease, background-color ${props => props.theme.transitionTiming},
    color ${props => props.theme.transitionTiming};
  @media (${props => props.theme.breakpoint.xl.up}) {
    padding: ${props => props.theme.btnHoverPadding};
  }

  ${props =>
    props.$isLoading &&
    css`
      color: transparent !important;
      clip-path: polygon(0 0, calc(101% + 14px) 0, 101% 101%, 0 101%);
    `}
`;

export const Button = styled.div<Pick<ButtonProps, '$isSmall' | '$themeColorColor' | '$fullWidth'>>`
  ${StyledTypo.h6Css};
  ${ButtonBase};
  position: relative;
  z-index: 1;
  width: 100%;
  padding: ${props => props.theme.btnPaddingMobile};
  margin: 1px !important;
  overflow: hidden;
  color: ${props => props.theme.color[buttonThemes[props.$themeColorColor].text]};
  background-color: ${props => props.theme.color[buttonThemes[props.$themeColorColor].background.normal]};
  border: none;
  border-radius: ${({ theme }) => (theme.isBorderRadiusDisabled ? 0 : '25px')};
  outline: none;
  transition: ${props => props.theme.transitionTiming};
  @media (${props => props.theme.breakpoint.xl.up}) {
    padding: ${props => props.theme.btnPadding};
  }

  img {
    display: inline-block;
    vertical-align: middle;
  }
`;

type ButtonContainerProps = Pick<
  ButtonProps,
  'ref' | '$isLoading' | '$themeColorColor' | '$hoverThemeColor' | '$isSmall' | '$fullWidth' | '$minWidth'
>;

export const ButtonContainer = styled.button<ButtonContainerProps>`
  ${resetButtonStylingCSS};
  position: relative;
  display: inline-block;
  width: ${props => (props.$fullWidth ? '100%' : 'auto')};
  overflow: hidden;
  border-radius: ${({ theme }) => (theme.isBorderRadiusDisabled ? 0 : '25px')};

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    text-decoration: none;
    opacity: 1;

    ${ButtonHover} {
      clip-path: polygon(0 0, calc(101% + 14px) 0, 101% 101%, 0 101%);
    }
  }

  ${props =>
    props.$minWidth &&
    css`
      width: 100%;
      @media (${props => props.theme.breakpoint.xl.up}) {
        width: auto;
        min-width: 180px;
      }
    `};

  ${props =>
    props.$isLoading &&
    css`
      cursor: wait !important;

      &:active {
        pointer-events: none;
      }
    `}

  ${props =>
    props.$isSmall &&
    css`
      ${Button} {
        padding: ${props => props.theme.btnSmallPadding} !important;
        line-height: 1;
      }
      ${ButtonHover} {
        padding: 0 !important;
        line-height: 1;
      }
    `}

  &:active {
    ${ButtonHover} {
      background-color: ${props => props.theme.color[buttonHoverThemes[props.$hoverThemeColor].background.active]};
    }
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.2;

    * {
      cursor: not-allowed;
    }
  }
`;
